import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import syakuraLogo from "@/assets/syakura-logo.png";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Brand panel */}
      <div
        className="relative hidden w-1/2 flex-col justify-between p-12 lg:flex"
        style={{ background: "hsl(var(--sidebar))" }}
      >
        <div className="flex items-center gap-3">
          <img src={syakuraLogo} alt="Syakura Student Living" className="h-12 w-12 object-contain" />
          <div className="text-lg font-bold text-white">Syakura Student Living</div>
        </div>
        <div className="max-w-md">
          <h1 className="text-4xl font-extrabold leading-tight text-white">
            Setiap data yang kamu kelola, <span className="text-primary">membangun ekosistem kerja yang mudah</span>.
          </h1>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: "hsl(var(--sidebar-muted))" }}>
            Kelola proyek, kamar, penghuni, booking, pembayaran, hingga laporan investor
            dalam satu aplikasi internal. Akses dibatasi sesuai proyek yang ditugaskan.
          </p>
        </div>
        <div className="text-xs" style={{ color: "hsl(var(--sidebar-muted))" }}>
          Aplikasi internal — khusus admin & superadmin.
        </div>
      </div>

      {/* Form panel */}
      <div className="flex w-full items-center justify-center bg-background p-6 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg p-1.5"
              style={{ background: "hsl(var(--sidebar))" }}
            >
              <img src={syakuraLogo} alt="Syakura Student Living" className="h-full w-full object-contain" />
            </div>
            <span className="font-bold">Syakura Student Living</span>
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Masuk ke akun</h2>
          <p className="mt-1 text-sm text-muted-foreground">Gunakan email dan kata sandi yang diberikan.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@syakura.co.id"
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Kata Sandi</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Masuk
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
